import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUp, Trophy } from "lucide-react";
import TopBar from "../components/layout/TopBar";
import Speedometer from "../components/Speedometer";
import Sparkline from "../components/Sparkline";
import SignalTicker from "../components/SignalTicker";

import PillarCard from "../components/PillarCard";
import ScoreMovement from "../components/ScoreMovement";
import NicheComDrawer from "../components/NicheComDrawer";
import ForecastPanel from "../components/growth/ForecastPanel";
import DoNext from "../components/growth/DoNext";
import OpportunityFeed from "../components/growth/OpportunityFeed";
import IntelligenceFeed from "../components/growth/IntelligenceFeed";
import DataFreshness from "../components/growth/DataFreshness";
import SellerImpression from "../components/growth/SellerImpression";
import LocalPosition from "../components/growth/LocalPosition";
import CompetitorGap from "../components/growth/CompetitorGap";
import useApi from "../hooks/useApi";
import { useBranch } from "../context/BranchContext";
import { useAuth } from "../context/AuthContext";

export default function Overview() {
  const navigate = useNavigate();
  const { branchId, branch } = useBranch();
  const { user } = useAuth();
  const { data } = useApi(`/growth/overview?branch_id=${branchId}`, [branchId]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || "there").split(" ")[0];
  const hero = (
    <div className="mb-6 animate-rise">
      <h1 className="text-3xl font-extrabold tracking-tighter text-ink">{greeting}, {firstName}</h1>
      <p className="text-ink-2 mt-1">Your entire marketing presence for <span className="font-semibold text-ink">{branch?.name || "Padstow"}</span>, in one score.</p>
    </div>
  );

  if (!data) return <><TopBar /><div className="p-6 sm:p-8 max-w-[1400px]">{hero}<SkeletonGrid /></div></>;

  const moveMap = Object.fromEntries((data.movement || []).map((m) => [m.pillar, m.delta]));

  return (
    <>
      <TopBar />
      <div className="p-6 sm:p-8 max-w-[1400px]">
        {hero}

        {/* Row 1 — Growth Score + Growth Forecast */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-7 rounded-3xl p-6 sm:p-10 relative overflow-hidden flex flex-col items-center justify-center" style={{ background: "linear-gradient(160deg,#1E2415 0%,#0F130A 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,.05), 0 30px 60px -34px rgba(0,0,0,.6)" }} data-testid="growth-hero">
            <div className="blob" style={{ width: 520, height: 520, top: -260, left: "50%", transform: "translateX(-50%)", background: "radial-gradient(circle, rgba(147,192,32,.20), transparent 70%)" }} />
            <div className="relative z-10 w-full flex flex-col items-center">
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1" style={{ background: "rgba(182,225,79,.14)" }} data-testid="hero-status">
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#B6E14F" }} />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "#B6E14F" }}>{data.status}</span>
              </div>

              <div className="mt-2"><Speedometer value={data.growth_score} delta={data.trend} size={380} dark /></div>

              <div className="w-full max-w-lg mt-4 pt-6 grid grid-cols-3 text-center" style={{ borderTop: "1px solid rgba(255,255,255,.08)" }}>
                <div data-testid="hero-local">
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="text-2xl font-extrabold tracking-tighter text-white">#{data.local?.position ?? "—"}</span>
                    {data.local?.movement ? <span className="inline-flex items-center gap-0.5 text-xs font-bold" style={{ color: "#B6E14F" }}><ArrowUp size={12} strokeWidth={2.5} />{data.local.movement}</span> : null}
                  </div>
                  <div className="text-xs mt-1" style={{ color: "rgba(255,255,255,.5)" }}>of {data.local?.field_size ?? "—"} locally</div>
                </div>
                <div className="px-2" style={{ borderLeft: "1px solid rgba(255,255,255,.08)", borderRight: "1px solid rgba(255,255,255,.08)" }}>
                  <div className="text-2xl font-extrabold tracking-tighter text-white">{data.active_signals}</div>
                  <div className="text-xs mt-1" style={{ color: "rgba(255,255,255,.5)" }}>active signals</div>
                </div>
                <div>
                  <div className="flex justify-center h-8 items-center"><Sparkline data={data.spark} width={96} height={30} color="#B6E14F" strokeWidth={2} /></div>
                  <div className="text-xs mt-1" style={{ color: "rgba(255,255,255,.5)" }}>12-mo trend</div>
                </div>
              </div>

              <button className="btn-primary focus-ring mt-7 w-full max-w-lg justify-center !py-3" data-testid="see-improve" onClick={() => navigate("/app/forecast")}>See how to improve <ArrowRight size={16} strokeWidth={2} /></button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="lg:col-span-5">
            <ForecastPanel forecast={data.forecast} />
          </motion.div>
        </div>

        {/* Data freshness */}
        <div className="mt-5"><DataFreshness items={data.freshness} /></div>

        {/* Do this next */}
        <div className="mt-6"><DoNext doNext={data.do_next} /></div>

        {/* Growth opportunities */}
        <div className="mt-8">
          <OpportunityFeed opportunities={data.opportunities} count={data.opportunity_count} gain={data.opportunity_gain} />
        </div>

        {/* Score categories */}
        <div className="mt-8">
          <SignalTicker items={data.ticker} count={data.active_signals} />
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {data.pillars.map((p) => <PillarCard key={p.key} pillar={p} movement={moveMap[p.key]} />)}
          </div>
        </div>

        {/* Seller impression + Local position */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SellerImpression data={data.seller_impression} compact />
          <LocalPosition local={data.local} />
        </div>

        {/* Competitor gap */}
        <div className="mt-6"><CompetitorGap gap={data.competitor_gap} /></div>

        {/* What changed */}
        <div className="mt-6">
          <ScoreMovement movement={data.movement} net={data.net_movement} from={data.prev_score} to={data.growth_score} />
        </div>

        {/* NicheCom (connected customers) — slide-out drawer on the right edge */}
        {data.nichecom?.enabled && <NicheComDrawer data={data.nichecom} />}

        {/* Group standing */}
        {data.group && data.group.group_size > 1 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 card p-5" data-testid="group-standing">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center w-10 h-10 rounded-lg" style={{ background: "var(--grad-green-soft)" }}><Trophy size={19} color="#5F8213" strokeWidth={1.75} /></span>
                <div><div className="label-caps">In your group</div><div className="font-bold text-ink">{data.group.group_name}</div></div>
              </div>
              <div><div className="text-2xl font-extrabold tracking-tighter text-ink">#{data.group.group_rank}</div><div className="text-xs text-ink-2">of {data.group.group_size} branches</div></div>
              <div><div className="text-2xl font-extrabold tracking-tighter text-ink">{data.group.beats}</div><div className="text-xs text-ink-2">branches you're ahead of</div></div>
              <p className="ml-auto text-sm text-ink-2 max-w-xs">You're ahead of <span className="font-semibold" style={{ color: "#678A16" }}>{data.group.better_than_group}%</span> of branches in {data.group.group_name}.</p>
            </div>
          </motion.div>
        )}

        {/* Intelligence feed */}
        <div className="mt-8"><IntelligenceFeed items={data.feed} /></div>
      </div>
    </>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-12 gap-6 animate-pulse">
      <div className="col-span-7 h-64 rounded-xl" style={{ background: "#EDEEE9" }} />
      <div className="col-span-5 h-64 rounded-xl" style={{ background: "#EDEEE9" }} />
    </div>
  );
}
